import { computed, onMounted, reactive, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import {
    createBookingServiceType,
    getBookingAdminGrants,
    getBookingScheduleDates,
    getBookingServiceType,
    listBookingServiceTypes,
    updateBookingAdminGrants,
    updateBookingScheduleDates,
    updateBookingServiceType
} from '@/api/consultation/consultationApi';
import { usePermission } from '@/composables/usePermission';

const CATALOG_STARTS = ['10:00', '11:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:30', '19:30'];

export const BOOKING_WEEKDAYS = [
    { value: 1, label: '週一' },
    { value: 2, label: '週二' },
    { value: 3, label: '週三' },
    { value: 4, label: '週四' },
    { value: 5, label: '週五' },
    { value: 6, label: '週六' },
    { value: 0, label: '週日' }
];

export const BOOKING_PERIODS = [
    { value: 'morning', label: '上午', hint: '09:00–12:00' },
    { value: 'afternoon', label: '下午', hint: '12:00–17:00' },
    { value: 'evening', label: '晚上', hint: '17:00 以後' }
];

/**
 * @param {{ mode?: 'full' | 'window' | 'schedule' }} [options]
 * full：工程師完整參數（預約規則）
 * window：管理端僅可約區間／同時段上限（參數設置）
 * schedule：開班表／可排班日（與預約規則分開；有預約規則也可編）
 */
export function useBookingServiceSettings({ mode = 'full' } = {}) {
    const { canView: canViewFull, canUpdate: canUpdateFull } = usePermission('BookingParams');
    const { canView: canViewWindow, canUpdate: canUpdateWindow } = usePermission('BookingWindowSettings');
    const { canView: canViewSchedule, canUpdate: canUpdateSchedule } = usePermission('BookingScheduleManage');
    const isWindowMode = mode === 'window';
    const isScheduleMode = mode === 'schedule';
    const canView = computed(() => {
        if (isWindowMode) return canViewWindow.value;
        if (isScheduleMode) return canViewSchedule.value || canViewFull.value;
        return canViewFull.value;
    });
    const canUpdate = computed(() => {
        if (isWindowMode) return canUpdateWindow.value;
        if (isScheduleMode) return canUpdateSchedule.value || canUpdateFull.value;
        return canUpdateFull.value;
    });


    const loading = ref(false);
    const saving = ref(false);
    const savingDates = ref(false);
    const savingGrant = ref(false);
    const creating = ref(false);
    const extraStart = ref('');
    const services = ref([]);
    const selectedCode = ref('consultation');
    const month = ref(new Date().toISOString().slice(0, 7));
    const openDates = ref([]);
    const grants = ref([]);
    const grantDraft = reactive({
        core_member_id: '',
        permissions: []
    });
    const createForm = reactive({
        code: '',
        name: '',
        duration_min: 60,
        assignment_mode: 'auto',
        schedule_date_policy: 'required'
    });
    const meta = reactive({
        code: 'consultation',
        name: '財務諮詢',
        duration_min: 60,
        provider_capacity_per_slot: 1
    });
    const form = reactive({
        booking_window_min_days: 7,
        booking_window_max_days: 30,
        offline_capacity_per_slot: 2,
        online_supported: true,
        offline_supported: true,
        daily_slots: [...CATALOG_STARTS],
        blackout: {},
        assignment_mode: 'auto',
        schedule_date_policy: 'optional'
    });

    const serviceOptions = computed(() =>
        services.value.map((item) => ({
            value: item.code,
            label: item.name || item.code
        }))
    );
    const assignmentOptions = [
        { value: 'auto', label: '系統派單' },
        { value: 'pick', label: '學員自選' }
    ];
    const datePolicyOptions = [
        { value: 'optional', label: '未圈日期仍可排' },
        { value: 'required', label: '必須先圈日期' }
    ];
    const editDatePolicyOptions = [
        { value: 'optional', label: '這個月仍可排班' },
        { value: 'required', label: '這個月不能排班' }
    ];

    const blackoutKey = (weekday, period) => `${weekday}:${period}`;

    const slotOptions = computed(() => {
        const extra = form.daily_slots.filter((start) => !CATALOG_STARTS.includes(start));
        return [...CATALOG_STARTS, ...extra];
    });

    const applyPayload = (data) => {
        meta.code = data.code || 'consultation';
        meta.name = data.name || '財務諮詢';
        meta.duration_min = Number(data.duration_min || 60);
        meta.provider_capacity_per_slot = Number(data.provider_capacity_per_slot || 1);
        form.booking_window_min_days = Number(data.booking_window_min_days ?? 7);
        form.booking_window_max_days = Number(data.booking_window_max_days ?? 30);
        form.offline_capacity_per_slot = Number(data.offline_capacity_per_slot ?? 2);
        form.online_supported = Boolean(data.online_supported);
        form.offline_supported = Boolean(data.offline_supported);
        form.daily_slots = (data.daily_slots || [])
            .map((slot) => String(slot.start || ''))
            .filter(Boolean)
            .sort();
        form.blackout = {};
        (data.blackout_rules || []).forEach((rule) => {
            form.blackout[blackoutKey(rule.weekday, rule.period)] = true;
        });
        form.assignment_mode = data.assignment_mode === 'pick' ? 'pick' : 'auto';
        form.schedule_date_policy = data.schedule_date_policy === 'required' ? 'required' : 'optional';
    };

    const monthDays = computed(() => {
        const [year, mon] = String(month.value || '').split('-').map(Number);
        if (!year || !mon) return [];
        const count = new Date(year, mon, 0).getDate();
        return Array.from({ length: count }, (_, index) => {
            const day = String(index + 1).padStart(2, '0');
            return `${month.value}-${day}`;
        });
    });

    const loadDates = async () => {
        const res = await getBookingScheduleDates(selectedCode.value, month.value);
        if (res.status !== 1) throw new Error(res.message || '載入可排班日失敗');
        openDates.value = Array.isArray(res.data?.dates) ? [...res.data.dates] : [];
    };

    const loadGrants = async () => {
        const res = await getBookingAdminGrants(selectedCode.value);
        if (res.status !== 1) throw new Error(res.message || '載入行政權限失敗');
        grants.value = Array.isArray(res.data) ? res.data : [];
    };

    const loadService = async (code) => {
        const res = await getBookingServiceType(code);
        if (res.status !== 1) throw new Error(res.message || '載入失敗');
        selectedCode.value = code;
        applyPayload(res.data || {});
        if (!isWindowMode) {
            await Promise.all([loadDates(), loadGrants()]);
        }
    };

    const onSelectService = async (code) => {
        loading.value = true;
        try {
            await loadService(code);
        } catch (e) {
            message.error(e.message || '載入服務失敗');
        } finally {
            loading.value = false;
        }
    };

    const load = async () => {
        if (!canView.value) return;
        loading.value = true;
        try {
            const list = await listBookingServiceTypes();
            if (list.status !== 1) throw new Error(list.message || '載入服務失敗');
            services.value = Array.isArray(list.data) ? list.data : [];
            const code = services.value.some((item) => item.code === selectedCode.value)
                ? selectedCode.value
                : services.value[0]?.code || 'consultation';
            await loadService(code);
        } catch (e) {
            message.error(e.message || '載入失敗');
        } finally {
            loading.value = false;
        }
    };

    const toggleSlot = (start, checked) => {
        if (checked) {
            if (!form.daily_slots.includes(start)) form.daily_slots.push(start);
        } else {
            form.daily_slots = form.daily_slots.filter((item) => item !== start);
        }
        form.daily_slots.sort();
    };

    const addExtraSlot = () => {
        const start = String(extraStart.value || '').trim();
        if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(start)) {
            message.warning('時段請用 24 小時制，例如 20:30');
            return;
        }
        toggleSlot(start, true);
        extraStart.value = '';
    };

    const toggleBlackout = (weekday, period, checked) => {
        form.blackout[blackoutKey(weekday, period)] = checked;
    };

    const save = async () => {
        if (form.booking_window_max_days < form.booking_window_min_days) {
            message.warning('最遠可約天數不可小於最早可約天數');
            return;
        }

        let payload;
        if (isWindowMode) {
            payload = {
                booking_window_min_days: Number(form.booking_window_min_days),
                booking_window_max_days: Number(form.booking_window_max_days),
                offline_capacity_per_slot: Number(form.offline_capacity_per_slot),
                online_supported: form.online_supported,
                offline_supported: form.offline_supported
            };
        } else {
            if (!form.daily_slots.length) {
                message.warning('至少保留一個可預約時段');
                return;
            }

            const blackout_rules = [];
            BOOKING_WEEKDAYS.forEach((day) => {
                BOOKING_PERIODS.forEach((period) => {
                    if (form.blackout[blackoutKey(day.value, period.value)]) {
                        blackout_rules.push({ weekday: day.value, period: period.value });
                    }
                });
            });

            payload = {
                booking_window_min_days: Number(form.booking_window_min_days),
                booking_window_max_days: Number(form.booking_window_max_days),
                offline_capacity_per_slot: Number(form.offline_capacity_per_slot),
                online_supported: form.online_supported,
                offline_supported: form.offline_supported,
                daily_slots: form.daily_slots.map((start) => ({ start })),
                blackout_rules,
                assignment_mode: form.assignment_mode,
                schedule_date_policy: form.schedule_date_policy
            };
        }

        saving.value = true;
        try {
            const res = await updateBookingServiceType(selectedCode.value, payload);
            if (res.status !== 1) throw new Error(res.message || '儲存失敗');
            applyPayload(res.data || {});
            message.success('已儲存');
        } catch (e) {
            message.error(e.message || '儲存失敗');
        } finally {
            saving.value = false;
        }
    };

    const toggleDate = (date, checked) => {
        if (checked) {
            if (!openDates.value.includes(date)) openDates.value.push(date);
        } else {
            openDates.value = openDates.value.filter((item) => item !== date);
        }
        openDates.value.sort();
    };

    const saveDates = async () => {
        savingDates.value = true;
        try {
            const res = await updateBookingScheduleDates(selectedCode.value, {
                month: month.value,
                dates: openDates.value.filter((date) => date.startsWith(month.value))
            });
            if (res.status !== 1) throw new Error(res.message || '儲存失敗');
            openDates.value = Array.isArray(res.data?.dates) ? [...res.data.dates] : [];
            message.success('可排班日已更新');
        } catch (e) {
            message.error(e.message || '儲存可排班日失敗');
        } finally {
            savingDates.value = false;
        }
    };

    const saveGrant = async () => {
        const coreMemberId = grantDraft.core_member_id.trim();
        if (!coreMemberId) {
            message.warning('請填行政的會員編號');
            return;
        }
        savingGrant.value = true;
        try {
            const res = await updateBookingAdminGrants(selectedCode.value, {
                core_member_id: coreMemberId,
                permissions: [...grantDraft.permissions]
            });
            if (res.status !== 1) throw new Error(res.message || '儲存失敗');
            grants.value = Array.isArray(res.data) ? res.data : [];
            grantDraft.core_member_id = '';
            grantDraft.permissions = [];
            message.success('行政權限已更新');
        } catch (e) {
            message.error(e.message || '儲存行政權限失敗');
        } finally {
            savingGrant.value = false;
        }
    };

    const createService = async ({ stay = false } = {}) => {
        const previous = selectedCode.value;
        if (!/^[a-z][a-z0-9_]*$/.test(createForm.code)) {
            message.warning('代碼請用小寫英文開頭，只能有英文、數字與底線');
            return;
        }
        if (!createForm.name.trim()) {
            message.warning('請填服務名稱');
            return;
        }
        creating.value = true;
        try {
            const res = await createBookingServiceType({
                code: createForm.code.trim(),
                name: createForm.name.trim(),
                duration_min: Number(createForm.duration_min),
                booking_window_min_days: 7,
                booking_window_max_days: 30,
                offline_capacity_per_slot: 1,
                online_supported: true,
                offline_supported: false,
                daily_slots: CATALOG_STARTS.map((start) => ({ start })),
                blackout_rules: [],
                assignment_mode: createForm.assignment_mode,
                schedule_date_policy: createForm.schedule_date_policy
            });
            if (res.status !== 1) throw new Error(res.message || '建立失敗');
            createForm.code = '';
            createForm.name = '';
            await load();
            if (stay && previous) {
                await loadService(previous);
            } else if (res.data?.code) {
                await loadService(res.data.code);
            }
            message.success('服務項目已建立');
        } catch (e) {
            message.error(e.message || '建立服務失敗');
        } finally {
            creating.value = false;
        }
    };

    const permissionLabel = (value) =>
        ({ view: '查看', set_dates: '圈日期', proxy_schedule: '代排' }[value] || value);

    watch(month, () => {
        if (isWindowMode || !selectedCode.value || loading.value) return;
        loadDates().catch((e) => message.error(e.message || '載入可排班日失敗'));
    });

    onMounted(async () => {
        if (isScheduleMode) {
            if (!canView.value) return;
            selectedCode.value = 'consultation';
            loading.value = true;
            try {
                await loadDates();
            } catch (e) {
                message.error(e.message || '載入可排班日失敗');
            } finally {
                loading.value = false;
            }
            return;
        }
        await load();
    });

    return {
        canUpdate,
        loading,
        saving,
        savingDates,
        savingGrant,
        creating,
        extraStart,
        selectedCode,
        month,
        openDates,
        grants,
        grantDraft,
        createForm,
        meta,
        form,
        serviceOptions,
        assignmentOptions,
        datePolicyOptions,
        editDatePolicyOptions,
        slotOptions,
        monthDays,
        onSelectService,
        toggleSlot,
        addExtraSlot,
        toggleBlackout,
        save,
        toggleDate,
        saveDates,
        saveGrant,
        createService,
        permissionLabel
    };
}
